import { type ReactElement, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ROUTES } from '@/constants/routes';
import { useExtendTrial } from '@/hooks/data/use-billing';
import { formatCalendarDate } from '@/lib/format';
import { TypedApiError, typedErrorMessage } from '@/lib/typed-api-error';

export const Route = createFileRoute('/_authenticated/extend-trial')({
  staticData: { hideAppShell: true, title: 'route.extend_trial.title' },
  component: ExtendTrialPage,
});

// A refusal's title says what happened and its detail what to do next.
const toastRefusal = (error: unknown, t: TFunction<'nav'>): void => {
  if (error instanceof TypedApiError) {
    toast.error(error.message, { description: error.detail });
    return;
  }
  toast.error(typedErrorMessage(error, t('extend_trial.failed')));
};

function ExtendTrialPage(): ReactElement {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const { mutateAsync: extendTrial } = useExtendTrial();
  const claimedRef = useRef(false);

  useEffect(() => {
    // The link claims once: a StrictMode re-run would be refused as already used.
    if (claimedRef.current) return;
    claimedRef.current = true;

    const claim = async (): Promise<void> => {
      try {
        const { trial_ends_at } = await extendTrial();
        toast.success(
          t('extend_trial.extended', {
            date: formatCalendarDate(trial_ends_at),
          }),
        );
      } catch (error) {
        toastRefusal(error, t);
      }
      await navigate({ to: ROUTES.CONNECTORS, replace: true });
    };
    void claim();
  }, [extendTrial, navigate, t]);

  return (
    <div className="bg-bg-page flex min-h-svh items-center justify-center p-6">
      <div className="text-fg-3 text-fs-13 flex items-center gap-2">
        <LoaderCircle className="size-4 animate-spin" />
        {t('extend_trial.extending')}
      </div>
    </div>
  );
}
